
import { hamletUser } from "../../common/hamlet/hamlet-user";
import { trackEvent, piiScrub } from "../../common/analytics";
import markdownIt from "markdown-it";
import hljs from "highlight.js";

const md = markdownIt({
  highlight: function (str: string, lang: string) {
    if (lang && hljs && hljs.getLanguage(lang) && lang !== "yaml") {
      try {
        return hljs.highlight(lang, str).value;
      } catch (__) { }
    }
    return "";
  },
});

const productNames: { [key: string]: string } = {
  "jira-software": "Jira Software",
  "jira-work-management": "Jira Work Management",
  "jira-service-management": "Jira Service Management",
  "jira-product-discovery": "Jira Product Discovery",
  devops: "DevOps",
  confluence: "Confluence",
};

const svgs = {
  linkArrow: `<svg class="link-arrow-image" width="11px" height="8px" viewBox="0 0 11 8" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g class="group-path" transform="translate(-138.000000, -586.000000)" fill="#0052CC"><path d="M145.2803,586.507862 L144.2193,587.568863 L145.9393,589.287862 L138.7503,589.287862 C138.3363,589.287862 138.0003,589.623862 138.0003,590.037862 C138.0003,590.451862 138.3363,590.787862 138.7503,590.787862 L145.9393,590.787862 L144.2193,592.507862 L145.2803,593.568863 L148.8103,590.037862 L145.2803,586.507862 Z"></path></g></g></svg>`,
  linkArrowOut: `<svg class="link-arrow-external-image" width="8px" height="7px" viewBox="0 0 8 7" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd"><g class="group-path" transform="translate(-167.000000, -650.000000)" fill="#0052CC"><path d="M169.077333,650 L169.077333,651.487884 L171.489775,651.487884 L167.24717,655.729347 C166.956527,656.01998 166.956527,656.491144 167.24717,656.781777 C167.392988,656.926598 167.582452,657 167.7739,657 C167.964356,657 168.15382,656.926598 168.299638,656.781777 L172.54125,652.540315 L172.54125,654.951679 L174.029187,654.951679 L174.029187,650 L169.077333,650 Z"></path></g></g></svg>`,
  video: `<svg width="24px" height="24px" viewBox="0 0 24 24" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink"><circle id="Oval" fill="#0052CC" cx="12" cy="12" r="12"></circle><polygon id="Triangle-Copy" fill="#FFFFFF" points="17 12 9 16 9 8"></polygon></svg>`
};

const linkClassTypes = {
  "default": "",
  "null": "",
  "linkPrimary": "button item-link button--one button--regular button--primary",
  "linkSecondary": "button item-link button--one button--regular button--secondary",
  "buttonprimary": "button item-link button--one button--regular button--primary",
  "buttonsecondary": "button item-link button--one button--regular button--secondary",
  "video": "video",
  "arrow": "link-arrow",
  "link-arrow": "link-arrow",
  "linkArrow": "link-arrow",
  "arrowout": "link-arrow-out",
  "link-arrow-out": "link-arrow-out",
  "linkArrowOut": "link-arrow-out"
};

const linkIconTypes = {
  "default": "",
  "null": "",
  "linkPrimary": "",
  "linkSecondary": "",
  "buttonprimary": "",
  "buttonsecondary": "",
  "video": svgs.video,
  "arrow": svgs.linkArrow,
  "link-arrow": svgs.linkArrow,
  "linkArrow": svgs.linkArrow,
  "arrowout": svgs.linkArrowOut,
  "link-arrow-out": svgs.linkArrowOut,
  "linkArrowOut": svgs.linkArrowOut
};

type Item = {
  uuid: string;
  title: string,
  description: string;
  link: string;
  linkOpenNewTab: boolean;
  logoMaxHeight: string;
  logoMaxWidth: string
  showAsBackgroundImage: boolean;
  imageSize: string;
  filterCloudProduct: string;
  backgroundColor: string;
  customField1: string;
  customField2: string;
  customField3: string;
  logo: string;
  image: string;
  linkType: keyof typeof linkIconTypes;
  linkLabel: string;
  linkButton: keyof typeof linkIconTypes;
  date: string;
  searchString: string;
  unsortedCategories: [{ [key: string]: string }];
  customSorts: { [key: string]: string };
  selectedProduct: string[];
  category: string[];
  features: string[];
  status: string[];
  product: string[];
};

type CategoryOption = {
  label: string;
  toggleDefinition: string;
  amount: number;
}

type Category = {
  label: string;
  filterType: "checkbox" | "radio";
  resetOthers: boolean;
  emptyCategory: string;
  defaultOption: string;
  defaultDefinition: string;
  toggleDefinition: boolean;
  options: {
    [key: string]: CategoryOption
  };
};

type Categories = {
  [key: string]: Category;
}

type LabelsType = {
  noResults: any;
  result: any;
  results: any;
  closeLabel: any;
  copyLabel: any;
  copied: any;
  linkLabel: string;
  shareLabel: string;
  shareBelowLabel: string;
  allCategoriesLabel: string;
  selected: string;
};

type FilterSortSearchParams = {
  jsonURL: any;
  jsonPath: any;
  itemsArr: any;
  categories: Categories;
  hideSearch: any;
  updateCategoryCounts: boolean;
  hideCategories: boolean;
  hideCategoryCounts: boolean;
  showStaticCategory: boolean;
  featuredPagesSize: number;
  labels: LabelsType;
  attributesArray: any;
  modalAttributesArray: any;
  showShadows: any;
  makeItemClickable: any;
  isIntegrations: any;
  gridClasses: any;
  linkToModal: any;
  modalLinkParam: any;
  scrollUpToTopOnCategoryClick: boolean;
  logoMaxWidth: number;
  logoMaxHeight: number;
  useJSON: boolean;
  activeCategories: {
    [key: string]: Category[]
  };
  paginateItems: number;
  paginatePosition?: "top" | "bottom" | "both";
  paginateAlign: string;
  templatesLinkButtonText: string;
  templatesModalConfluence: any;
  templatesModalJiraSoftware: any;
  templatesModalJiraServiceManagement: any;
  templatesModalJiraWorkManagement: any;
  templatesTryLink: any;
  titleFieldId: string;
  descriptionFieldId: string;
  htmlUnescapedFieldId: string;
  logoFieldId: string;
  imageFieldId: string;
  linkFieldId: string;
  dateFieldId: string;
  linkLabelFieldId: string;
  customField1Id: string;
  customField2Id: string;
  customField3Id: string;
  customCategoryKey: string;
  customCategory2Key: string;
  customCategoryAllKey: string;
  keepSorted: any;
  sortKey: string;
  originalPageSlug: string;
  useStandaloneURLAsParam: boolean;
};

export default class FilterSortSearch {
  domRoot: HTMLElement;
  params: FilterSortSearchParams;
  items: any;
  categories: Categories;
  itemsArea: HTMLElement;
  clearInput: HTMLElement;
  searchInput: HTMLInputElement;
  displayButtons: NodeListOf<HTMLElement>;
  numResultsLabel: HTMLElement;
  resultsLabel: HTMLElement;
  categoriesContainers: NodeListOf<HTMLElement>;
  activeCategoriesLabel: HTMLElement;
  showAllButton: HTMLElement;
  hideSearch: any;
  updateCategoryCounts: boolean;
  hideCategories: boolean;
  hideCategoryCounts: boolean;
  showStaticCategory: boolean;
  featuredPagesSize: number;
  showingFeatured: boolean = true;
  labels: LabelsType;
  itemAttributes: any;
  modalAttributes: any;
  showShadows: any;
  makeItemClickable: any;
  isIntegrations: any;
  gridClasses: any;
  linkToModal: any;
  modalLinkParam: any;
  scrollUpToTopOnCategoryClick: boolean;
  logoMaxWidth: number;
  logoMaxHeight: number;
  modalContent: any;
  displayStyle: string = "search-grid";
  useJSON: boolean;
  totalUnfiltered: any;
  activeCategories: {
    [key: string]: Category[]
  };
  paginateItems: number;
  paginatePosition?: "top" | "bottom" | "both";
  paginateAlign: string;
  currentPage: number = 1;
  isJiraTemplates: boolean;
  templatesLinkButtonText: string;
  templatesModal_confluence: any;
  templatesModal_jirasoftware: any;
  templatesModal_jiraservicemanagement: any;
  templatesModal_jiraworkmanagement: any;
  templatesTryLink: any;
  templatesKeys: { [name: string]: string } = {
    "jira-software": "jira-software",
    "jira-service-management": "jira-servicedesk",
    "jira-work-management": "jira-core",
  };
  linkLabel: string;
  ids: { [key: string]: string };
  keepSorted: any;
  customSortKey?: string | null;
  sortKey: string;
  originalPageSlug: string;
  useStandaloneURLAsParam: boolean;
  sortKeyParsed: any;
  modalElement: HTMLDivElement = document.createElement("div");
  isLoggedIn: boolean = false;
  sitesProducts: string[] = [];
  activeDropdown: string = '';
  activeDismissHandler?: (e: MouseEvent) => void;

  constructor(domRoot: HTMLElement, params: FilterSortSearchParams) {
    this.domRoot = domRoot;
    this.params = params;
    this.items = params.itemsArr;
    this.categories = params.categories ? params.categories : {};
    this.itemsArea = domRoot.querySelector(".all-items") as HTMLElement;
    this.clearInput = domRoot.querySelector(".clear-items") as HTMLElement;
    this.searchInput = domRoot.querySelector(".search-items") as HTMLInputElement;
    this.displayButtons = document.getElementsByName("radio-items");
    this.numResultsLabel = domRoot.querySelector(".num-results") as HTMLElement;
    this.resultsLabel = domRoot.querySelector(".results-label") as HTMLElement;
    this.categoriesContainers = domRoot.querySelectorAll(
      ".categories-containers"
    );
    this.activeCategoriesLabel = domRoot.querySelector(".active-category") as HTMLElement;
    this.showAllButton = domRoot.querySelector(".show-all") as HTMLElement;
    this.hideSearch = params.hideSearch;
    this.updateCategoryCounts = params.updateCategoryCounts;
    this.hideCategories = params.hideCategories;
    this.hideCategoryCounts = params.hideCategoryCounts;
    this.showStaticCategory = params.showStaticCategory;
    this.featuredPagesSize = params.featuredPagesSize;
    this.labels = params.labels;
    this.itemAttributes = params.attributesArray;
    this.modalAttributes = params.modalAttributesArray;
    this.showShadows = params.showShadows;
    this.makeItemClickable = params.makeItemClickable;
    this.isIntegrations = params.isIntegrations;
    this.gridClasses = params.gridClasses;
    this.linkToModal = params.linkToModal;
    this.modalLinkParam = params.modalLinkParam;
    this.scrollUpToTopOnCategoryClick = params.scrollUpToTopOnCategoryClick;
    this.logoMaxWidth = params.logoMaxWidth;
    this.logoMaxHeight = params.logoMaxHeight;
    this.useJSON = params.useJSON;
    this.totalUnfiltered = this.items.length;
    this.activeCategories = params.activeCategories;
    this.paginateItems = Number(params.paginateItems);
    this.paginatePosition = params.paginatePosition;
    this.paginateAlign = params.paginateAlign;
    this.isJiraTemplates = this.modalAttributes.includes(
      "productTemplatesModal"
    );
    this.templatesLinkButtonText = params.templatesLinkButtonText;
    this.templatesModal_confluence = params.templatesModalConfluence;
    this.templatesModal_jirasoftware = params.templatesModalJiraSoftware;
    this.templatesModal_jiraservicemanagement =
      params.templatesModalJiraServiceManagement;
    this.templatesModal_jiraworkmanagement =
      params.templatesModalJiraWorkManagement;
    this.templatesTryLink = params.templatesTryLink;
    this.linkLabel = params.labels.linkLabel || "";
    this.ids = {
      title: params.titleFieldId || "title",
      description: params.descriptionFieldId || "description",
      htmlUnescaped: params.htmlUnescapedFieldId || "config",
      logo: params.logoFieldId || "logo",
      image: params.imageFieldId || "image",
      link: params.linkFieldId || "url",
      dateLabel: params.dateFieldId || "dateLabel",
      linkLabel: params.linkLabelFieldId || "linkLabel",
      customField1: params.customField1Id || "customField1",
      customField2: params.customField2Id || "customField2",
      customField3: params.customField3Id || "customField3",
      customCategoryKey: params.customCategoryKey || "color",
      customCategory2Key: params.customCategory2Key || "color",
      customCategoryAllKey: params.customCategoryAllKey || "product",
    };
    this.keepSorted = params.keepSorted;
    this.sortKey =
      params.sortKey == params.dateFieldId ? "sorterDate" : params.sortKey;
    this.sortKeyParsed = this.sortKey.replace("customSorts.", "");
    this.originalPageSlug = params.originalPageSlug;
    this.useStandaloneURLAsParam = params.useStandaloneURLAsParam;

    if (this.isJiraTemplates) {
      const hamletUserCheck = () => {
        const isDebugMode = window.location.search.includes("debug=true");
        return hamletUser.getStargateProfile(true, isDebugMode).then(() => {
          this.isLoggedIn = hamletUser.isLoggedIn() || false;
          this.sitesProducts =
            this.sortSiteData(hamletUser.getSites(), "products") || [];
          this.renderAll();
        });
      };

      hamletUserCheck();
    }
    document.body.appendChild(this.modalElement);
    if (!this.showStaticCategory) {
      if (this.getParam(["search"])) {
        this.searchInput.value = decodeURIComponent(this.getParam(["search"]) as string);
      }
      this.clearInput.addEventListener("click", () => {
        this.searchInput.value = "";
        history.replaceState(null, "", "?" + this.getParam(["search"], true));
        this.renderAll();
      });

      ["keyup", "change"].forEach((handler) => {
        this.searchInput.addEventListener(handler, () => {
          this.scrollToTopOfPage();
          this.currentPage = 1;
          let newParams = this.getParam(["search", "page"], true);
          history.replaceState(
            null,
            "",
            `?${newParams}&search=${encodeURIComponent(this.searchInput.value)}`
          );
          this.renderAll();
        });
      });
      ["click", "change"].forEach((handler) => {
        this.searchInput.addEventListener(handler, (e) => {
          const target = e.target as HTMLInputElement;
          this.trackEvents(target, {
            event: handler,
            eventComponent: "searchInput",
            eventContainer: "filterSortSearch",
            searchValue: piiScrub(target.value)
          });
        })
      })
    } else {
      this.scrollLoadImg();
    }

    if (
      this.paginateItems > 0 &&
      this.getParam(["page"]) &&
      !isNaN(Number(this.getParam(["page"])))
    ) {
      this.currentPage = Number(this.getParam(["page"]));
    }

    if (!this.showStaticCategory && !this.hideCategories) {
      Array.from(this.categoriesContainers).forEach((catContainer) => {
        catContainer.addEventListener("click", (e: MouseEvent) => {
          this.parseFilterItem(e);
        });
      });
    }

    window.addEventListener("click", this.activeDismissHandler = (e) => this.dismissHandler(e));

    for (let categoryKey in this.categories) {
      this.activeCategories[categoryKey] = [];
      let referenceCat = this.categories[categoryKey];
      let urlStartingDefault: any[] = [];
      let startingDefault =
        referenceCat.defaultOption && referenceCat.defaultOption.length
          ? [referenceCat.defaultOption]
          : "";

      if (this.getParam([categoryKey])) {
        const urlSubCats = (this.getParam([categoryKey]) as string).split(';');
        urlSubCats.forEach((urlSubCat: string) => {
          if (referenceCat.options[urlSubCat]) {
            urlStartingDefault.push(urlSubCat)
          }
        })
      }

      startingDefault = urlStartingDefault.length ? urlStartingDefault : startingDefault;

      if (startingDefault.length) {
        this.activeCategories[categoryKey] = this.activeCategories[categoryKey].concat(startingDefault);
      }

      if (
        !this.showStaticCategory &&
        !this.hideCategories
      ) {
        const categoriesMobileArea = this.domRoot.querySelector(".categories-mobile-" + categoryKey) as HTMLElement;

        if (categoriesMobileArea) {
          const self = this;

          categoriesMobileArea.addEventListener("click", (e) => {
            e.stopImmediatePropagation();

            const target = e.target as HTMLElement;

            if (target.hasAttribute('data-category')) {
              this.parseFilterItem(e);
            }

            if (target.classList.contains('select-style')) {
              if (target.classList.contains('active-menu')) {
                target.classList.remove('active-menu');
              }
              else {
                if (document.querySelector('.active-menu')) {
                  (document.querySelector('.active-menu') as HTMLElement).classList.remove('active-menu');
                }
                this.activeDropdown = categoryKey;
                target.classList.add('active-menu');
                target.focus();
              }
            }
          });
        }
      }
    }

    for (let d = 0; d < this.displayButtons.length; d++) {
      this.displayButtons[d].addEventListener("click", (e) => {
        this.displayStyle = (e.target as HTMLInputElement).value;
        this.renderAll();
      });
    }

    this.modalElement.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains("copy-yaml") || target.classList.contains("share-modal-link")) {
        this.copyToClipboard(target);
      } else if (target.classList.contains("close-modal")) {
        this.closeModal(target);
      } else if (target.tagName == "A") {
        let modalItem = this.modalContent;
        let trackObjects: { [key: string]: string } = {
          eventContainer: "filterModal",
          cardCategories: modalItem.customSorts,
          parentLabel: this.getItemAttr(modalItem, this.ids.title),
          parentDescription: this.getItemAttr(modalItem, this.ids.description),
        };
        this.trackEvents(target, trackObjects);
      }
    });

    window.addEventListener("scroll", () => {
      this.scrollLoadImg();
    });
    window.addEventListener("keyup", (e) => {
      if (e.key == "Escape" && this.modalContent) {
        this.closeModal({});
      }
    });

    if (this.useJSON) {
      if (this.getParam(["category"])) {
        this.activeCategories["category"] = [this.getParam(["category"])];
      }
      fetch(this.params.jsonURL)
        .then((response) => response.json())
        .then((data2) => {

          let data = data2;
          if (this.params.jsonPath.length) {
            data = this.getItemAttr(data2, this.params.jsonPath);
          }

          for (let d = 0; d < data.length; d++) {
            data[d].searchString = "";
            for (let key in data[d]) {
              if (typeof data[d][key] == "string") {
                data[d].searchString += data[d][key].toLowerCase() + " ";
              } else if (
                typeof data[d][key] == "object" &&
                data[d][key].toString() !== "[object Object]"
              ) {
                data[d].searchString += data[d][key].toString();
              }
            }
          }
          this.items = data;
          this.totalUnfiltered = data.length;
          this.renderAll();
          this.paramModal();
        });
    } else if (!this.showStaticCategory) {
      this.renderAll();
    } else if (this.showAllButton) {
      this.showAllButton.addEventListener("click", (e) => {
        const target = e.target as HTMLElement;
        this.showingFeatured = !this.showingFeatured;
        target.classList.toggle("revealed");
        this.renderAll();
      });
    }

    (Array.from(this.domRoot.querySelectorAll(".collapsible")) as HTMLElement[]).forEach(
      (collapser) => {
        collapser.addEventListener("click", (e) => {
          const target = e.target as HTMLElement;
          target.classList.toggle("collapsed");
        });
      }
    );

    if (this.domRoot.querySelector(".reset-filter")) {
      this.domRoot.querySelectorAll(".reset-filter").forEach(resetter => {
        resetter.addEventListener("click", () => {
          for (let act in this.activeCategories) {
            this.activeCategories[act] = [];
          }
          history.replaceState(null, "", "?");
          this.renderAll();
        })
      })

    }
    if (this.domRoot.querySelector(".sorter-button")) {
      (this.domRoot.querySelectorAll(".sorter-button") as unknown as HTMLInputElement[]).forEach(sorter => {
        sorter.addEventListener("click", (e) => {
          const target = e.target as HTMLInputElement;

          let allSorts = this.domRoot.querySelectorAll(".sorter-button");
          allSorts.forEach(allSort => { allSort.classList.remove('active') });

          this.customSortKey = target.value;

          target.classList.add('active');

          this.renderAll(target.dataset.sortDir == "desc");
          target.dataset.sortDir = target.dataset.sortDir == "asc" ? "desc" : "asc";
        })
      })

    }
    window.onload = () => {
      this.paramModal();
    };
  }

  dismissHandler(e: MouseEvent) {
    if (!this.activeDropdown) {
      return;
    }

    const activeMenu = document.body.getElementsByClassName(`categories-mobile-${this.activeDropdown}`)[0] as HTMLElement;
    if (!activeMenu) {
      return;
    }

    const targetInsideMenu = activeMenu?.offsetParent?.lastChild?.contains(e.target as Node) === true;

    if (!targetInsideMenu) {
      this.activeDropdown = '';
      this.renderAll();
    }
  }

  parseFilterItem(e: MouseEvent) {
    const target = e.target as HTMLElement;

    let isActive = (target.parentNode as HTMLElement).classList.contains("active");
    let category = target.getAttribute("data-category") as string;
    let subCat = target.getAttribute("data-subcategory") as string;
    let filterType = target.getAttribute("data-filtertype");
    let resetOthers = target.getAttribute("data-reset") == "true";

    if (target.hasAttribute("data-subcategory")) {
      if (filterType == "radio" && subCat.length) {
        this.activeCategories[category] = subCat.length ? [subCat] : [];
      } else if (filterType == "checkbox" && subCat.length && !isActive) {
        this.activeCategories[category].push(subCat);
      } else if (filterType == "checkbox" && subCat.length && isActive) {
        let indexCat = this.activeCategories[category].indexOf(subCat);
        this.activeCategories[category].splice(indexCat, 1);
      } else if (subCat.length < 1) {
        this.activeCategories[category] = [];
      }

      const joinedCats = this.activeCategories[category].join(';');
      let newParams = `${category}=${joinedCats}&${this.getParam(
        [category, "page"],
        true
      )}`;
      if (resetOthers) {
        newParams = `${category}=${joinedCats}`;
        this.resetOtherCategories(category);
      }
      history.replaceState(null, "", `?${newParams}`);
      if (this.scrollUpToTopOnCategoryClick) {
        this.scrollToTopOfPage();
      }
      this.currentPage = 1;
      this.renderAll();
    } else if (target.hasAttribute("data-sortable")) {
      this.customSortKey = target.getAttribute("data-sortable");
      this.renderAll();
    }

    this.trackEvents(target, {
      eventContainer: "filterCategory",
      clickedCategory: category,
      clickedSubCategory: subCat,
    });
  }

  sortSiteData(data: any[], type: string) {
    let array: any[] = [];
    data.forEach((site) => {
      array = array.concat(site[type]);
    });
    return array;
  }

  scrollToTopOfPage() {
    this.domRoot.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  getCategoryAmounts(categoriesObj: { [key: string]: any }, item: HTMLElement) {
    for (let sideCat in categoriesObj) {
      let cat = this.getItemAttr(item, sideCat);
      if (cat) {
        if (typeof cat === "object") {
          for (let b = 0; b < cat.length; b++) {
            let identifier = cat[b].key ? cat[b].key : cat[b];
            if (categoriesObj[sideCat].options[identifier]) {
              categoriesObj[sideCat].options[identifier].amount++;
            }
          }
        } else if (categoriesObj[sideCat].options[this.slugify(cat)]) {
          categoriesObj[sideCat].options[this.slugify(cat)].amount++;
        }
      }
    }
  }

  getFilteredItems(reverse?: boolean) {
    let categoriesObj = JSON.parse(JSON.stringify(this.categories)) as Categories;
    let searchInputValue = this.showStaticCategory
      ? ""
      : this.searchInput.value.toLowerCase();
    return new Promise((resolve) => {
      let featuredItems = this.items.slice(0, this.featuredPagesSize);
      let featItems =
        this.showStaticCategory && this.showingFeatured
          ? featuredItems
          : this.items;
      let items = featItems.filter((a: any) => {
        delete a.headerBeginner;
        if (a.unsortedCategories) {
          for (let u = 0; u < a.unsortedCategories.length; u++) {
            let cat = a.unsortedCategories[u];
            let key = Object.keys(cat)[0];
            if (a[key] && a[key].indexOf(cat[key]) === -1) {
              a[key].push(cat[key]);
            } else {
              a[key] = [cat[key]];
            }
          }
        }

        if (this.useJSON) {
          for (let key in categoriesObj) {
            let itemCat = this.getItemAttr(a, key);
            let slug = this.slugify(itemCat);
            if (
              !categoriesObj[key].options[slug] &&
              itemCat &&
              itemCat.length
            ) {
              categoriesObj[key].options[slug] = {
                label: itemCat,
                amount: 0,
              };
            }
          }
        }

        if (!this.updateCategoryCounts) {
          /* get amounts before they've been filtered */
          this.getCategoryAmounts(categoriesObj, a);
        }

        let isInSearchResults =
          a.searchString && a.searchString.indexOf(searchInputValue) > -1;
        return (
          isInSearchResults &&
          Object.keys(this.activeCategories).every((b) => {
            return (
              this.activeCategories[b].length === 0 ||
              this.activeCategories[b].some((c: any) => {
                let matchLabel = categoriesObj[b].options[c]
                  ? categoriesObj[b].options[c].label
                  : c;
                let itemCat = this.getItemAttr(a, b);
                let itemCatMatcher =
                  itemCat && itemCat.label ? itemCat.label : itemCat;
                let isMatchers =
                  itemCatMatcher && itemCatMatcher.some
                    ? itemCatMatcher.some((item: any) => {
                      return (
                        item == matchLabel ||
                        (itemCatMatcher && itemCatMatcher.indexOf(c) > -1) ||
                        item.label == matchLabel
                      );
                    })
                    : this.getItemAttr(a, b) === matchLabel ||
                    (typeof this.getItemAttr(a, b) == "string" &&
                      this.getItemAttr(a, b).indexOf(c) > -1);
                return isMatchers;
              })
            );
          })
        );
      });

      if (this.updateCategoryCounts) {
        /* get amounts after they've been filtered */
        items.forEach((a: any) => {
          this.getCategoryAmounts(categoriesObj, a);
        });
      }

      if (this.keepSorted || this.customSortKey) {
        let sortKey = this.customSortKey ? this.customSortKey : this.sortKey;
        items.sort((a: any, b: any) => {
          let aVal = this.getItemAttr(a, sortKey)
            ? this.getItemAttr(a, sortKey)
            : "z";
          let bVal = this.getItemAttr(b, sortKey)
            ? this.getItemAttr(b, sortKey)
            : "z";
          if (aVal < bVal) {
            return -1;
          }
          if (aVal > bVal) {
            return 1;
          }
          if (aVal == bVal) {
            return 0;
          }
          return aVal - bVal;
        });
      }

      if (reverse) {
        items = items.reverse();
      }

      resolve({
        items: items,
        categoriesObj: categoriesObj,
      });
    });
  }

  getPaginationLink(i: number, dontClick?: boolean, customText?: string) {
    let pageLink = document.createElement("a");
    pageLink.innerHTML = customText ? customText : i as unknown as string;

    if (dontClick) {
      pageLink.className = "disabled";
    } else {
      pageLink.className = this.currentPage == i ? "active" : "";
      pageLink.onclick = () => {
        let newParams = `${this.getParam(["page"], true)}&page=${i}`;
        history.replaceState(null, "", `?${newParams}`);
        this.scrollToTopOfPage();
        this.currentPage = i;
        this.renderAll();
      };
    }

    return pageLink;
  }

  getPagination(totalItems: number, kind: string) {
    let totalPages = Math.ceil(totalItems / this.paginateItems);
    let maxPages = 5;
    let pagination = document.createElement("div");
    pagination.className = `pagination ${this.paginateAlign} ${kind}`;

    let leftArrow = this.getPaginationLink(
      this.currentPage - 1,
      this.currentPage <= 1,
      '<svg width="24" height="24" viewBox="0 0 24 24" role="presentation"><path fill="currentColor" fill-rule="evenodd" d="M9.005 10.995l4.593-4.593a.99.99 0 111.4 1.4l-3.9 3.9 3.9 3.9a.99.99 0 01-1.4 1.4L9.005 12.41a1 1 0 010-1.414z"></path></svg>'
    );
    pagination.appendChild(leftArrow);

    let startPage: number;
    let endPage: number;

    if (totalPages <= maxPages) {
      startPage = 1;
      endPage = totalPages;
    } else {
      let lowerBound = Math.floor(maxPages / 2);
      let upperBound = Math.ceil(maxPages / 2) - 1;
      if (this.currentPage <= lowerBound) {
        startPage = 1;
        endPage = maxPages;
      } else if (this.currentPage + upperBound >= totalPages) {
        startPage = totalPages - maxPages + 1;
        endPage = totalPages;
      } else {
        startPage = this.currentPage - lowerBound;
        endPage = this.currentPage + upperBound;
      }
    }
    let pages = Array.from(Array(endPage + 1 - startPage).keys()).map(
      (i) => startPage + i
    );

    if (!pages.includes(1)) {
      pagination.appendChild(this.getPaginationLink(1));
      if (pages[0] > 2 && totalPages > maxPages) {
        let ellipse = document.createElement("div");
        ellipse.innerText = "...";
        pagination.appendChild(ellipse);
      }
    }
    pages.forEach((page) => {
      pagination.appendChild(this.getPaginationLink(page));
    });
    if (!pages.includes(totalPages)) {
      if (totalPages - pages[pages.length - 1] > 1 && totalPages > maxPages) {
        let lastEllipse = document.createElement("div");
        lastEllipse.innerText = "...";
        pagination.appendChild(lastEllipse);
      }
      pagination.appendChild(this.getPaginationLink(totalPages));
    }

    let rightArrow = this.getPaginationLink(
      this.currentPage + 1,
      this.currentPage >= totalPages,
      '<svg width="24" height="24" viewBox="0 0 24 24" role="presentation"><path fill="currentColor" fill-rule="evenodd" d="M14.995 10.995a1 1 0 010 1.414l-4.593 4.593a.99.99 0 01-1.4-1.4l3.9-3.9-3.9-3.9a.99.99 0 011.4-1.4l4.593 4.593z"></path></svg>'
    );
    pagination.appendChild(rightArrow);

    return pagination;
  }

  renderAll(reverse?: boolean) {
    this.itemsArea.innerHTML = `<div class="loading"></div>`;

    this.getFilteredItems(reverse).then((resolve: any) => {
      let categoriesObj = resolve.categoriesObj;
      let items = resolve.items;
      let totalItems = items.length;

      if (this.paginateItems > 0) {
        items = items.slice(
          (this.currentPage - 1) * this.paginateItems,
          this.paginateItems * this.currentPage
        );
      }

      let numItems = items.length;

      if (numItems === 0) {
        this.itemsArea.innerHTML = `<div class="column"><h3>${this.labels.noResults}</h3></div>`;
      } else {
        this.itemsArea.innerHTML = ``;

        const itemsFragment = document.createDocumentFragment();

        if (
          this.paginateItems > 0 &&
          (this.paginatePosition == "top" || this.paginatePosition == "both")
        ) {
          itemsFragment.appendChild(this.getPagination(totalItems, "top"));
        }

        for (let i = 0; i < numItems; i++) {
          itemsFragment.appendChild(this.getItemHTML(items[i]));
        }

        if (
          this.paginateItems > 0 &&
          (this.paginatePosition == "bottom" || this.paginatePosition == "both")
        ) {
          itemsFragment.appendChild(this.getPagination(totalItems, "bottom"));
        }

        this.itemsArea.appendChild(itemsFragment);
      }

      if (!this.hideCategories && !this.showStaticCategory) {
        for (let cat in categoriesObj) {
          const cssClass = cat.replace(/\./g, '');
          const catsDesk = this.domRoot.querySelector(".catparent-" + cssClass) as HTMLElement;
          const catsDeskOldUL = catsDesk.querySelector("ul");
          const catsDeskUlClass = catsDesk.getAttribute("data-ulclass");
          const catsDeskUlType = catsDesk.getAttribute("data-picktype");
          const catsMobile = this.domRoot.querySelector(
            ".categories-mobile-" + cssClass
          );
          const catsMobContainer = document.createElement('div');
          const catsDeskContainer = document.createElement('ul');
          const showCatCounts = !this.hideCategoryCounts;
          const isActive = this.activeCategories[cat].length == 0;

          const activeMobileLabel = this.activeCategories[cat].length == 1 ? categoriesObj[cat].options[this.activeCategories[cat][0]].label : this.activeCategories[cat].length + " " + this.labels.selected;

          const emptyMobileLabel = this.activeCategories[cat].length ? activeMobileLabel : categoriesObj[cat].emptyCategory;
          const emptyAmount = this.updateCategoryCounts
            ? totalItems
            : this.totalUnfiltered;

          catsDesk.removeChild(catsDeskOldUL);
          catsDeskContainer.className = `${catsDeskUlClass} ${catsDeskUlType}`;
          
          catsDeskContainer.innerHTML = `<li class="empt ${isActive ? 'active' : ''}">
            <span data-filtertype="${categoriesObj[cat].filterType
            }" data-category="${cat}" data-subcategory=""> ${categoriesObj[cat].emptyCategory
            }${showCatCounts ? ` (${emptyAmount})` : ``}</span>
          </li>`;

          if (catsMobile) {
            catsMobile.innerHTML = `
            <label for="mobile-select-${cat}">${categoriesObj[cat].label}</label>
            <button aria-label="${emptyMobileLabel}" id="mobile-select-${cat}" class="select-style ${this.activeDropdown == cat ? 'active-menu' : ''} ">${emptyMobileLabel}</button>`;

            catsMobContainer.innerHTML = `<div class="${isActive ? 'active' : ''}">
              <input name="${cat}" type="${categoriesObj[cat].filterType}" id="${cat}-null" data-filtertype="${categoriesObj[cat].filterType}" data-category="${cat}" data-subcategory="" value="null" ${isActive ? 'checked' : ''} />
              <label for="${cat}-null">
              ${categoriesObj[cat].emptyCategory}${showCatCounts ? ` (${this.totalUnfiltered})` : ``
              }
              </label>
            </div>`;
          }
          if (isActive && this.categories[cat].toggleDefinition) {
            (this.domRoot.querySelector(
              ".category-definition-" + cat
            ) as HTMLElement).innerHTML = this.categories[cat].defaultDefinition;
          }
          for (let option in categoriesObj[cat].options) {
            let subCat = categoriesObj[cat].options;
            let isactiveCategories =
              this.activeCategories[cat].indexOf(option) > -1;

            catsDeskContainer.innerHTML += `<li class="sub-${option} ${isactiveCategories
              ? `active`
              : ``
              }">
              <span data-reset="${categoriesObj[cat].resetOthers
              }" data-filtertype="${categoriesObj[cat].filterType
              }" data-category="${cat}" data-subcategory="${option}">
                ${subCat[option].label}${showCatCounts ? ` (${subCat[option].amount})` : ``
              }
              </span>
            </li>`;
            if (isactiveCategories && this.categories[cat].toggleDefinition) {
              (this.domRoot.querySelector(
                ".category-definition-" + cat
              ) as HTMLElement).innerHTML =
                this.categories[cat].options[option].toggleDefinition;
            }

            if (catsMobile) {
              catsMobContainer.innerHTML += `
              <div class="${isactiveCategories ? "active" : ""}">
                <input name="${cat}" type="${categoriesObj[cat].filterType}" id="${cat}-${option}" data-reset="${categoriesObj[cat].resetOthers
                }" ${isactiveCategories ? `checked` : ``} data-category="${cat}" data-filtertype="${categoriesObj[cat].filterType}" data-subcategory="${option}" value="${option}">
                <label for="${cat}-${option}">
                ${subCat[option].label}${showCatCounts ? ` (${subCat[option].amount})` : ``
                }
              </label>
            </div>`;
            }
          }

          catsDesk.appendChild(catsDeskContainer);
          if (catsMobile) {
            catsMobile.appendChild(catsMobContainer);
          }
        }

        this.numResultsLabel.innerHTML = totalItems;
        this.resultsLabel.innerHTML =
          totalItems === 1 ? this.labels.result : this.labels.results;
        this.activeCategoriesLabel.innerHTML = "";
        let label = "";
        for (let active in this.activeCategories) {
          if (this.activeCategories[active].length) {
            for (let a = 0; a < this.activeCategories[active].length; a++) {
              let option = this.activeCategories[active][a];
              label += `<span>${categoriesObj[active].options[option].label}</span>`;
            }
          } else {
            label += `<span>${categoriesObj[active].emptyCategory}</span>`;
          }
          this.activeCategoriesLabel.innerHTML = label;
        }
      }

      this.scrollLoadImg();
    });
  }

  paramModal() {
    const pParamValue = this.getParam(["p"]);
    if (pParamValue && pParamValue.length) {
      if (!(this.isJiraTemplates)) {
        this.renderItemModal();
      } else {
        const item = this.items.find((i) => {
          return i[this.modalLinkParam] === pParamValue
        });
        let event = document.createEvent('Event');
        event.dataset = {
          templateKey: item.templateId,
          templateId: item.templateId,
          bluePrintModule: item.templateId,
          templateName: item.filterTitle,
          productKey: `${this.templatesKeys[item.filterCloudProduct]}.ondemand`
        };
        event.initEvent('productTemplateModal', true, true);
        document.body.dispatchEvent(event);
      }
    } else if (this.modalContent) {
      this.closeModal({});
    }
  }

  renderItemModal() {
    let foundParam:string = '';
    for (let m = 0; m < this.items.length; m++) {
      if (
        this.getItemAttr(this.items[m], this.modalLinkParam) ==
        this.getParam(["p"])
      ) {
        let foundItem = this.items[m];
        foundParam = this.getItemAttr(foundItem, this.modalLinkParam);
        this.modalContent = foundItem;
        // for initial load of standalone URLs
        if (this.useStandaloneURLAsParam) {
          window.history.replaceState(null, null, `${this.originalPageSlug}/${this.getItemAttr(foundItem, "pageSlug")}${window.location.search}`);
        }
        break;
      }
    }

    if (this.modalContent) {
      const card = this.domRoot.querySelector(`div[data-param="${foundParam}"]`);
      if (card) {
        card.scrollIntoView();
        window.scrollBy(0, -150);
      }
      document.body.style.overflowY = 'hidden';
      this.modalElement.appendChild(this.getModalHTML());
      this.modalElement.querySelector('.modal-inner').focus();
    }
    this.scrollLoadImg();
  }

  closeModal(e: Event) {
    let modalItem = this.modalContent;
    let trackObjects: { [key: string]: string } = {
      eventContainer: "filterModal",
      eventComponent: "closeModalButton",
      cardCategories: modalItem ? modalItem.customSorts : {},
      parentLabel: this.getItemAttr(modalItem, this.ids.title),
      parentDescription: this.getItemAttr(modalItem, this.ids.description),
    };
    this.trackEvents(e.target as HTMLElement, trackObjects);
    this.modalContent = {};
    document.body.style.overflowY = "auto";
    this.modalElement.innerHTML = "";
    window.history.pushState(null, "", `${this.originalPageSlug}?${this.getParam(['p'], true)}`);
  }

  launchModal(e, item, itemNode) {
    const target = e.target as HTMLElement;
    if (target.classList.contains("modal-pop")) {
      itemNode.classList.remove("showalltags");
      if (target.classList.contains("modal-pop")) {

        let searchStringAdditional = this.getParam(["p"], true);
        let newURL = `?${searchStringAdditional}&p=${this.getItemAttr(item, this.modalLinkParam)}`;
        if (this.useStandaloneURLAsParam) {
          newURL = `${this.originalPageSlug}/${this.getItemAttr(item, "pageSlug")}?${searchStringAdditional}&p=${this.getItemAttr(item, this.modalLinkParam)}`;
        }
        window.history.pushState(null, "", newURL);

        this.paramModal();
      } else if (target.getAttribute("data-search")) {
        window.scrollTo(0, this.domRoot.offsetTop - 70);
        let a = new Event("change");
        this.searchInput.value = decodeURIComponent(
          target.getAttribute("data-search") || ''
        );
        this.searchInput.dispatchEvent(a);
      } else if (target.classList.contains("expandtags")) {
        itemNode.classList.add("showalltags");
      }
    }
  }

  getItemHTML(item: Item) {
    let itemNode = document.createElement("div");
    let linkItem = this.makeItemClickable;
    let itemClass =
      this.displayStyle == "search-grid" ? this.gridClasses : "xs-none-bottom";
    let atts = ``;
    for (var i = 0; i < this.itemAttributes.length; i++) {
      if (this[this.itemAttributes[i]]) {
        atts += this[this.itemAttributes[i]](item);
      }
    }
    if (this.linkToModal) {

      itemNode.onclick = (e) => {
        this.launchModal(e, item, itemNode);
      };
      itemNode.onkeyup = (e) => {
        if (document.activeElement == e.target && e.key == "Enter") {
          this.launchModal(e, item, itemNode);
        }
      };
            
      if (this.makeItemClickable) {
        itemNode.setAttribute("data-param", this.getItemAttr(item, this.modalLinkParam));
        itemClass += " modal-pop";
        itemNode.setAttribute("tabindex", "0");
      }
    }
    itemNode.className = `pi column ${this.displayStyle} ${itemClass} ${this.showShadows ? `shadows` : ``
      } ${this.isIntegrations ? `integration` : ``}`;
    itemNode.innerHTML = `
    ${linkItem && !this.linkToModal
        ? `<a class="outer-link doNotAutoTrack" ${this.getItemAttr(item, "linkOpenNewTab") ? 'target="_blank"' : ""
        } href="${this.getItemAttr(item, this.ids.link)}">`
        : ``
      }
    <div class="inner">
      ${atts}
      </div>
    ${linkItem && !this.linkToModal ? `</a>` : ``}`;

    itemNode.addEventListener("click", (e) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName == "A" ||
        target.tagName == "BUTTON" ||
        target.classList.contains("modal-pop")
      ) {
        let trackObjects: { [key: string]: string } = {
          eventContainer: "filterCard",
          cardCategories: item.customSorts,
          parentLabel: this.getItemAttr(item, this.ids.title),
          parentDescription: this.getItemAttr(item, this.ids.description),
        };
        if (this.isJiraTemplates) {
          trackObjects.product = this.getItemAttr(item, "filterCloudProduct");
          trackObjects.productKey = this.getItemAttr(
            item,
            "filterCloudProduct"
          );
          trackObjects.templateId = this.getItemAttr(item, "templateId");
          trackObjects.category =
            this.getItemAttr(item, "templateId") +
            "_" +
            this.getItemAttr(item, "filterCloudProduct").replace(/-/g, "_");
        }
        this.trackEvents(target, trackObjects);
      }
    });
    return itemNode;
  }

  getModalHTML() {
    const item = this.modalContent;
    let filterModalInner = document.createElement("div");
    let filterModalOuter = document.createElement("div");
    filterModalInner.innerHTML = "";

    this.modalAttributes.forEach((modalAttribute) => {
      if (this[modalAttribute]) {
        if (this[modalAttribute]() && this[modalAttribute]().then) {
          this[modalAttribute](item, null, true).then((resolve) => {
            filterModalInner.innerHTML += resolve;
          });
        } else {
          filterModalInner.innerHTML += this[modalAttribute](item, null, true);

        }
      }
    });
    let backingA = document.createElement("a");
    let cornerClose = document.createElement("a");
    cornerClose.href = "javascript:void(0);";
    cornerClose.className = "corner-close";
    backingA.onclick = (backingA) => {
      this.closeModal(backingA);
    };
    cornerClose.onclick = (cornerClose) => {
      this.closeModal(cornerClose);
    };
    cornerClose.innerText = this.labels.closeLabel;
    cornerClose.setAttribute("aria-label", this.labels.closeLabel);
    let filterModalContent = document.createElement("div");
    filterModalContent.className = "filter-modal-content";
    filterModalOuter.className = "modal-outer";
    filterModalInner.className = "modal-inner";
    filterModalInner.setAttribute("tabindex", "0");
    filterModalContent.appendChild(backingA);
    filterModalOuter.appendChild(filterModalInner);
    filterModalOuter.appendChild(cornerClose);
    filterModalContent.appendChild(filterModalOuter);

    return filterModalContent;
  }

  customAttr(item: Item, int: number) {
    return `<div class="custom${int}">${this.getItemAttr(
      item,
      this.ids["custom" + int]
    )}</div>`;
  }
  customField1(item: Item) {
    let field = this.getItemAttr(item, this.ids.customField1);
    return field
      ? `<p class="custom-field-1 custom-${this.slugify(field)}">${field}</p>`
      : ``;
  }
  customField2(item: Item) {
    let field = this.getItemAttr(item, this.ids.customField2);
    return field
      ? `<p class="custom-field-2 custom-${this.slugify(field)}">${field}</p>`
      : ``;
  }
  customField3(item: Item) {
    let field = this.getItemAttr(item, this.ids.customField2);
    return field
      ? `<div class="custom-field-3 custom-${this.slugify(field).substring(0, 40)}">${field}</div>`
      : ``;
  }
  title(item: Item) {
    return `<h4 class="title keep ${this.makeItemClickable ? "text-n900" : ""
      }">${this.getItemAttr(item, this.ids.title)}</h4>`;
  }
  description(item: Item, isFirstSentence = true) {
    let content = this.getItemAttr(item, this.ids.description)
      ? this.getItemAttr(item, this.ids.description)
      : "";
    if (isFirstSentence) {
      let delimiter = /\.(\s|\r|\n|<\/p>)/;
      let splits = content.split(delimiter);
      let splitMatch = content.match(delimiter);
      content = splits[0] + (splitMatch ? splitMatch[0] : "");
    }
    let el =
      content.includes("<p") || content.includes("<li")
        ? "div"
        : "p";
    return `<${el} class="description ${this.makeItemClickable ? "text-n900" : ""
      }">${content}</${el}>`;
  }
  descriptionFirstSentence(item: Item) {
    return this.description(item, true);
  }
  htmlUnescaped(item: Item) {
    return `<p class="unescaped">${this.escapeHtml(
      this.getItemAttr(item, this.ids.htmlUnescaped)
    )}</p>`;
  }
  logo(item: Item, fullSpan: boolean) {
    let maxWide = this.logoMaxWidth ? this.logoMaxWidth : "80px";
    let maxTall = this.logoMaxHeight ? this.logoMaxHeight : "80px";
    let imageTag = `<img style="max-width: ${this.getItemAttr(item, "logoMaxWidth")
      ? this.getItemAttr(item, "logoMaxWidth")
      : maxWide
      }; max-height: ${this.getItemAttr(item, "logoMaxHeight")
        ? this.getItemAttr(item, "logoMaxHeight")
        : maxTall
      };" class="keep" data-lz="${this.getItemAttr(
        item,
        this.ids.logo
      )}" alt="${this.getItemAttr(
        item,
        this.ids.title
      )}" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAJCAYAAAA7KqwyAAAAAXNSR0IArs4c6QAAAAlwSFlzAAALEwAACxMBAJqcGAAAAVlpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IlhNUCBDb3JlIDUuNC4wIj4KICAgPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4KICAgICAgPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIKICAgICAgICAgICAgeG1sbnM6dGlmZj0iaHR0cDovL25zLmFkb2JlLmNvbS90aWZmLzEuMC8iPgogICAgICAgICA8dGlmZjpPcmllbnRhdGlvbj4xPC90aWZmOk9yaWVudGF0aW9uPgogICAgICA8L3JkZjpEZXNjcmlwdGlvbj4KICAgPC9yZGY6UkRGPgo8L3g6eG1wbWV0YT4KTMInWQAAABFJREFUKBVjYBgFoyFAhRAAAAJJAAFEkgypAAAAAElFTkSuQmCC">`;
    return `<div class="keep logo-area ${this.getItemAttr(item, "imageSize")} ${this.getItemAttr(item, "backgroundColor")
      ? this.getItemAttr(item, "backgroundColor")
      : "n10"
      } ${fullSpan ? "full-span" : ""}" ${this.getItemAttr(item, "showAsBackgroundImage")
        ? `data-lz-bg="${this.getItemAttr(item, this.ids.logo)}"`
        : ""
      }>
        ${this.getItemAttr(item, "showAsBackgroundImage") ? "" : imageTag}
      </div>`;
  }
  logoFullSpan(item: Item) {
    return this.logo(item, true);
  }
  tags(item: Item) {
    let htmls = "";
    let extended = "";
    htmls += '<div class="tags">';
    for (var t = 0; t < item.tags.length; t++) {
      let comma = "";
      if (t == 3 && item.tags.length > 4 && t !== item.tags.length - 1) {
        comma = ", ";
      }
      let buttonHTML = `<button data-search="${item.tags[t]}">${item.tags[t]}${comma}</button>`;
      if (t < 3) {
        htmls += buttonHTML;
      } else {
        extended += buttonHTML;
      }
    }
    if (extended.length) {
      htmls += `<div><button class="expandtags">...</button>
                  <div class="extended">${extended}</div>
                  </div>`;
    }
    htmls += "</div>";
    return htmls;
  }
  image(item: Item, fullSpan: boolean) {
    let imageTag = `<img style="max-width: ${this.getItemAttr(item, "logoMaxWidth")
      ? this.getItemAttr(item, "logoMaxWidth")
      : this.logoMaxWidth
      }; max-height: ${this.getItemAttr(item, "logoMaxHeight")
        ? this.getItemAttr(item, "logoMaxHeight")
        : this.logoMaxHeight
      };" class="keep" data-lz="${this.getItemAttr(
        item,
        this.ids.image
      )}" alt="${this.getItemAttr(
        item,
        this.ids.title
      )}" src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAJCAYAAAA7KqwyAAAAAXNSR0IArs4c6QAAAAlwSFlzAAALEwAACxMBAJqcGAAAAVlpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IlhNUCBDb3JlIDUuNC4wIj4KICAgPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4KICAgICAgPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIKICAgICAgICAgICAgeG1sbnM6dGlmZj0iaHR0cDovL25zLmFkb2JlLmNvbS90aWZmLzEuMC8iPgogICAgICAgICA8dGlmZjpPcmllbnRhdGlvbj4xPC90aWZmOk9yaWVudGF0aW9uPgogICAgICA8L3JkZjpEZXNjcmlwdGlvbj4KICAgPC9yZGY6UkRGPgo8L3g6eG1wbWV0YT4KTMInWQAAABFJREFUKBVjYBgFoyFAhRAAAAJJAAFEkgypAAAAAElFTkSuQmCC">`;
    return `<div class="keep img-area ${this.getItemAttr(item, "imageSize")} ${this.getItemAttr(item, "backgroundColor")
      ? this.getItemAttr(item, "backgroundColor")
      : ""
      } ${fullSpan ? "full-span" : ""}" style="min-height: ${this.getItemAttr(item, "logoMaxHeight")
        ? this.getItemAttr(item, "logoMaxHeight")
        : this.logoMaxHeight
      };" ${this.getItemAttr(item, "showAsBackgroundImage")
        ? `data-lz-bg="${this.getItemAttr(item, this.ids.image)}"`
        : ""
      }>
        ${this.getItemAttr(item, "showAsBackgroundImage") ? "" : imageTag}
      </div>`;
  }
  imageFullSpan(item: Item) {
    return this.image(item, true);
  }
  link(item: Item, linkType = "default", isModal: boolean = false) {
    let individualLinkType = this.getItemAttr(item, "linkType") && this.getItemAttr(item, "linkType") != "default" ? this.getItemAttr(item, "linkType") : linkType;

    let linkLabel = this.getItemAttr(item, this.ids.linkLabel)
      ? this.getItemAttr(item, this.ids.linkLabel)
      : this.labels.linkLabel;

    let linkHref = this.getItemAttr(item, this.ids.link);
    if (this.linkToModal && !isModal) {
      linkHref = "javascript:void(0);";
    }

    let linkIcon = linkIconTypes[individualLinkType];
    let linkClass = linkClassTypes[individualLinkType];
    let modalLink = this.linkToModal ? "modal-pop button-modal-popper" : "";

    if ((this.linkToModal && !isModal) || (linkLabel.length && linkHref.length) || (linkLabel.length && !isModal && this.makeItemClickable)) {
      var tag = (!isModal && this.makeItemClickable) ? "span" : "a";
      return `<${tag} ${this.getItemAttr(item, "linkOpenNewTab") ? 'target="_blank"' : ""
        } href="${linkHref}" class="doNotAutoTrack item-link button ${modalLink} ${linkClass}" data-param="${this.getItemAttr(item, this.modalLinkParam)}">${linkLabel}${linkIcon}</${tag}>`;
    }
    return "";
  }
  linkArrow(item: Item, linkType = "arrow", isModal: boolean = false) {
    return this.link(item, linkType, isModal);
  }
  linkArrowOut(item: Item, linkType = "arrowout", isModal: boolean = false) {
    return this.link(item, linkType, isModal);
  }
  linkVideo(item: Item, linkType = "video", isModal: boolean = false) {
    return this.link(item, linkType, isModal);
  }
  linkPrimary(item: Item, linkType = "buttonprimary", isModal: boolean = false) {
    return this.link(item, linkType, isModal);
  }
  linkSecondary(item: Item, linkType = "buttonsecondary", isModal: boolean = false) {
    return this.link(item, linkType, isModal);
  }
  extraLink2(item: Item) {
    if (this.getItemAttr(item, "extraLink2URL") && this.getItemAttr(item, "extraLink2Label")) {
      return `<div class="extra-link-2">
      <a href="${this.getItemAttr(item, "extraLink2URL")}" target="_blank" class="doNotAutoTrack item-link button link-arrow">${this.getItemAttr(item, "extraLink2Label")} ${linkIconTypes.linkArrow}</a>
      </div>`;
    } else {
      return "";
    }
  }
  extraLink3(item: Item) {
    if (this.getItemAttr(item, "extraLink3URL") && this.getItemAttr(item, "extraLink3Label")) {
      return `<div class="extra-link-3">
      <a href="${this.getItemAttr(item, "extraLink3URL")}" target="_blank" class="doNotAutoTrack item-link button link-arrow">${this.getItemAttr(item, "extraLink3Label")} ${linkIconTypes.linkArrow}</a>
      </div>`;
    } else {
      return "";
    }
  }
  extraLink4(item: Item) {
    if (this.getItemAttr(item, "extraLink4URL") && this.getItemAttr(item, "extraLink4Label")) {
      return `<div class="extra-link-4">
      <a href="${this.getItemAttr(item, "extraLink4URL")}" target="_blank" class="doNotAutoTrack item-link button link-arrow">${this.getItemAttr(item, "extraLink4Label")} ${linkIconTypes.linkArrow}</a>
      </div>`;
    } else {
      return "";
    }
  }
  dateLabel(item: Item) {
    return `<p class="date ${this.makeItemClickable ? "text-n900" : ""
      }">${this.getItemAttr(item, this.ids.dateLabel)}</p>`;
  }
  category(item: Item) {
    let activeCategory = "";
    for (var act in this.activeCategories) {
      if (item[act]) {
        let itemAct = item[act];
        let identifier = itemAct[0].key ? itemAct[0].key : itemAct[0];
        activeCategory = this.categories[act].options[identifier]
          ? this.categories[act].options[identifier].label
          : "";
        for (var f in itemAct) {
          let catIdent = itemAct[f].key ? itemAct[f].key : itemAct[f];
          if (this.activeCategories[act].indexOf(itemAct[f]) > -1) {
            activeCategory = this.categories[act].options[catIdent].label;
            break;
          }
        }
        break;
      }
    }
    return `<h5 class="eyebrows ${this.makeItemClickable ? "text-n900" : ""
      }">${activeCategory}</h5>`;
  }
  customCategory(item: Item | {} = {}) {
    let cat = item[this.ids.customCategoryKey]
      ? item[this.ids.customCategoryKey][0]
      : "";
    let correspondingCat = this.categories[this.ids.customCategoryKey];
    return cat
      ? `<p class="custom-category custom-${this.slugify(cat)}">${correspondingCat && correspondingCat.options[cat]
        ? correspondingCat.options[cat].label
        : cat
      }</p>`
      : ``;
  }
  customCategory2(item: Item | {} = {}) {
    let cat = item[this.ids.customCategory2Key]
      ? item[this.ids.customCategory2Key][0]
      : "";
    let correspondingCat = this.categories[this.ids.customCategory2Key];
    return cat
      ? `<p class="custom-category2 custom-${this.slugify(cat)}">${correspondingCat && correspondingCat.options[cat]
        ? correspondingCat.options[cat].label
        : cat
      }</p>`
      : ``;
  }
  customCategoryAll(item: Item | {} = {}) {
    let cats = item[this.ids.customCategoryAllKey];
    let correspondingCat = this.categories[this.ids.customCategoryAllKey];
    return cats
      ? `<p class="custom-category-all custom-${this.ids.customCategoryAllKey
      }" data-label="${this.labels.allCategoriesLabel}">${cats
        .map((cat) => {
          let label = correspondingCat && correspondingCat.options[cat]
            ? correspondingCat.options[cat].label
            : cat
          return `<span class="${this.slugify(label)}">${label}</span>`;
        })
        .join("")}</p>`
      : ``;
  }
  shareButton(item: Item | {} = {}, shareType = "normal") {
    let shareURL = shareType == "rss" ? `${window.location.origin}${window.location.pathname}.rss` : window.location.href;
    return `<div class="share-buttons">
      <label for="share-modal-url">${this.labels.shareLabel}</label>
      <input readonly id="share-modal-url" value="${shareURL}" />
      <button class="button button--primary button--one button--regular share-modal-link">${this.labels.copyLabel}</button>
      <div class="below-share">
        ${this.labels.shareBelowLabel}
      </div>
    </div>`;
  }
  shareButtonRSS(item: Item | {} = {}) {
    return this.shareButton(item, "rss");
  }
  bitbucketPipe(item: Item) {
    return new Promise((resolve) => {
      if (item) {
        fetch(
          `https://bitbucket.org/${item.repositoryPath}/raw/${item.version}/README.md`
        )
          .then((response) => response.text())
          .then(async (data) => {

            var markedData = `<div class="version">${item.version}</div><div class="markdown">`;
            markedData += md.render(data);
            var matches = markedData.match(
              /<code class="language-yaml">([\s\S]*?)<\/code>/gm
            );
            if (matches) {
              for (var i = 0; i < matches.length; i++) {
                var matched = matches[i]
                  .replace('<code class="language-yaml">', "")
                  .replace("</code>", "");
                var styledMarkedData = this.escapeHtml(matched);
                markedData = markedData.replace(matched, styledMarkedData);
              }
            }
            markedData += `</div><div class="buttons-area">
              <a class="doNotAutoTrack button button--small button--one button--primary" href="https://bitbucket.org/${item.repositoryPath}">${this.labels.linkLabel}</a>
            </div>`;
            resolve(markedData);
          });
      }
    });
  }

  productTemplatesModal(item: Item) {
    let product: string = this.getItemAttr(item, "filterCloudProduct")
      .toString()
      .toLowerCase()
      .replace(/-/g, "");
    let modalContent = this["templatesModal_" + product];
    return modalContent;
  }

  templateSelectorButton(item: Item) {
    let productName = this.getItemAttr(item, "filterCloudProduct");
    let linkProduct = this.templatesKeys[productName]
      ? this.templatesKeys[productName]
      : productName;
    let productKey = `${linkProduct}.ondemand`;
    let isModalLink =
      this.isLoggedIn && this.sitesProducts.includes(productKey);
    const continueUrl = encodeURIComponent(`${window.location.href.split(/[?#]/)[0]}?p=${item.uuid}`);
    let tryButton =
      linkProduct !== "devops"
        ? `<div class="text-center"><a class="${isModalLink ? "modal-pop button-modal-popper" : ""
        } doNotAutoTrack standalone-template-button button button--regular button--one button--primary" 
                data-param="${this.getItemAttr(
          item,
          this.modalLinkParam
        )}" data-template-name="${this.getItemAttr(
          item,
          this.ids.title
        )}" data-product-key="${productKey}" data-template-key="${this.getItemAttr(
          item,
          "templateId"
        )}" data-blue-print-module="${this.getItemAttr(
          item,
          "templateId"
        )}"
                ${isModalLink ? "" :
          `href="${this.templatesTryLink}?bundle=${productName}&edition=free&templateId=${this.getItemAttr(
            item,
            "templateId"
          )}&continue=${continueUrl}"`
        }>${this.templatesLinkButtonText}</a></div>`
        : ``;

    return tryButton;
  }

  templateSelectorButtons(item: Item) {
    let linkLabel = this.getItemAttr(item, this.ids.linkLabel)
      ? this.getItemAttr(item, this.ids.linkLabel)
      : this.labels.linkLabel;
    return `<div class="buttons-overlay">
      ${this.templateSelectorButton(item)}
      <a class="doNotAutoTrack button button--regular button--one button--secondary" href="${this.getItemAttr(
      item,
      this.ids.link
    )}">${linkLabel}</a>
    </div>`;
  }

  product(item: Item) {
    return `<h5 class="product eyebrows">${productNames[this.getItemAttr(item, "filterCloudProduct")]
      }</h5>`;
  }

  copyToClipboard(copyButton: HTMLElement) {
    let modalItem = this.modalContent;
    let trackObjects: { [key: string]: string } = {
      eventContainer: "filterModal",
      eventComponent: "copyShareLinkButton",
      cardCategories: modalItem.customSorts,
      parentLabel: this.getItemAttr(modalItem, this.ids.title),
      parentDescription: this.getItemAttr(modalItem, this.ids.description),
    };
    let textSelect = copyButton.previousElementSibling as HTMLInputElement;
    textSelect.select();
    document.execCommand("copy");
    copyButton.innerHTML = this.labels.copied;
    this.trackEvents(copyButton, trackObjects);
    setTimeout(() => {
      copyButton.innerHTML = this.labels.copyLabel;
    }, 3000);
  }

  scrollLoadImg() {
    let winTop = window.scrollY;
    let winBot = window.innerHeight;
    let winVis = winTop + winBot;
    let unLoaded = document.querySelectorAll("[data-lz], [data-lz-bg]") as unknown as HTMLImageElement[];

    if (unLoaded.length) {
      for (let l = 0; l < unLoaded.length; l++) {
        let itemTop = this.offsetTopFind(unLoaded[l], 0);
        let itemHeight = itemTop + unLoaded[l].offsetHeight;
        if (
          this.modalContent !== false ||
          (itemTop < winVis && itemHeight > winTop)
        ) {
          let dataAttr = unLoaded[l].getAttribute("data-lz-bg")
            ? "data-lz-bg"
            : "data-lz";
          let src = unLoaded[l].getAttribute(dataAttr) as string;
          unLoaded[l].removeAttribute(dataAttr);
          if (dataAttr == "data-lz-bg") {
            unLoaded[l].style.backgroundImage = `url(${src})`;
          } else {
            unLoaded[l].src = src;
          }
        }
      }
    }
  }

  offsetTopFind(el: HTMLElement, withHeight = 0) {
    let yPosition = withHeight;
    while (el.offsetParent as HTMLElement !== null) {
      yPosition += el.offsetTop;
      el = el.offsetParent as HTMLElement;
    }
    return yPosition;
  }

  getItemAttr(item: Item, identifier: string) {
    if (item) {
      let syncData: Object | string = item;
      let keyVal = "";
      identifier = identifier.toString();
      let idKeys = identifier.split(".");
      while (idKeys.length > 0) {
        keyVal = idKeys[0];
        syncData = syncData && syncData[keyVal] ? syncData[keyVal] : "";
        idKeys.shift();
      }
      if (typeof syncData == "boolean") {
        syncData = syncData.toString();
      }
      return syncData;
    }
    return false;
  }

  escapeHtml(content: string) {
    return content
      .replace(/&quot;(.*?)&quot;/g, '<span class="s">&quot;$1&quot;</span>')
      .replace(/'(.*?)'/g, "<span class=\"s\">'$1'</span>")
      .replace(/# (.*?)(\r|\n)/g, '<span class="d"># $1</span>$2')
      .replace(
        /^(\s*)(\- ){0,1}([^#:]*?)(:|\n|\r)/gm,
        '$1$2<span class="k">$3</span>$4'
      )
      .replace(/  /g, "&nbsp;&nbsp;")
      .replace(/(\n+)/g, "<br>");
  }

  slugify(label: string = "") {
    return label
      .toString()
      .toLowerCase()
      .replace(/[^a-z0-9]/g, "");
  }

  getParam(paramsArray: any[], getAllExcept?: boolean) {
    let query = window.location.search.substring(1).replace(/\?/g, "");
    let vars = query.split("&");
    let returner: any = false;
    let newString = [];
    for (let i = 0; i < vars.length; i++) {
      let pair = vars[i].split("=");
      if (getAllExcept && pair[0] && !paramsArray.includes(pair[0])) {
        newString.push(`${pair[0]}=${pair[1]}`);
      } else if (paramsArray.includes(pair[0])) {
        vars.splice(i, 1);
        returner = pair[1];
      }
    }
    return getAllExcept ? newString.join("&") : returner;
  }

  resetOtherCategories(current: string) {
    for (var cat in this.activeCategories) {
      if (cat != current) {
        this.activeCategories[cat] = [];
      }
    }
  }

  trackEvents(target: HTMLElement, additionalProps?: any) {
    if (target) {
      let trackingBase: { [key: string]: any } = {
        eventComponent: "linkButton",
        activeCategories: this.activeCategories,
        label: target.innerText,
      };
      for (var key in additionalProps) {
        if (additionalProps.hasOwnProperty(key))
          trackingBase[key] = additionalProps[key];
      }
      trackEvent(additionalProps && additionalProps.event ? additionalProps.event : "clicked", trackingBase);
    }
  }
}
